
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { Lock, Shield } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface SecureAdminAccessProps {
  setIsAdmin: (isAdmin: boolean) => void;
  children?: React.ReactNode;
}

const SecureAdminAccess: React.FC<SecureAdminAccessProps> = ({ 
  setIsAdmin,
  children 
}) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { user, session } = useAuth();
  const { toast } = useToast();

  // Check if user is already an admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user || !session) {
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase.rpc('is_admin', { 
          user_uuid: user.id 
        });

        if (error) {
          console.error('Error checking admin status:', error);
          setIsLoading(false);
          return;
        }

        if (data) {
          setIsAdmin(true);
          setIsAuthenticated(true);
          toast({
            title: "Admin Access Granted",
            description: "Welcome back, admin.",
            variant: "default",
          });
        }
      } catch (error) {
        console.error('Error in admin check:', error);
      }
      
      setIsLoading(false);
    };

    checkAdminStatus();
  }, [user, session, setIsAdmin, toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Use environment variable or fallback to secure default
    const adminPassword = process.env.REACT_APP_ADMIN_PASSWORD || 'SecurePlantAdmin2024!';
    
    if (password === adminPassword) {
      // Set temporary admin bypass in session storage (more secure than localStorage)
      sessionStorage.setItem('temp_admin_access', 'true');
      sessionStorage.setItem('temp_admin_timestamp', Date.now().toString());
      
      setIsAdmin(true);
      setIsAuthenticated(true);
      
      // Log admin access attempt
      if (user) {
        try {
          await supabase.rpc('log_admin_activity', {
            p_admin_user_id: user.id,
            p_action: 'temp_admin_access_granted',
            p_details: { method: 'password_bypass', timestamp: new Date().toISOString() }
          });
        } catch (error) {
          console.warn('Could not log admin activity:', error);
        }
      }
      
      toast({
        title: "Temporary Admin Access Granted",
        description: "You have temporary admin access for this session.",
        variant: "default",
      });
    } else {
      setError('Incorrect password. Access denied.');
      
      // Log failed access attempt
      if (user) {
        try {
          await supabase.rpc('log_admin_activity', {
            p_admin_user_id: user.id,
            p_action: 'temp_admin_access_denied',
            p_details: { method: 'password_bypass', timestamp: new Date().toISOString() }
          });
        } catch (error) {
          console.warn('Could not log admin activity:', error);
        }
      }
    }
  };

  // Check for temporary admin access
  useEffect(() => {
    const tempAccess = sessionStorage.getItem('temp_admin_access');
    const timestamp = sessionStorage.getItem('temp_admin_timestamp');
    
    if (tempAccess === 'true' && timestamp) {
      const accessTime = parseInt(timestamp);
      const now = Date.now();
      const fourHours = 4 * 60 * 60 * 1000; // 4 hours in milliseconds
      
      if (now - accessTime < fourHours) {
        setIsAdmin(true);
        setIsAuthenticated(true);
      } else {
        // Expired temporary access
        sessionStorage.removeItem('temp_admin_access');
        sessionStorage.removeItem('temp_admin_timestamp');
      }
    }
  }, [setIsAdmin]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <>{children}</>;
  }

  if (!user) {
    return (
      <div className="flex justify-center items-center min-h-[60vh] p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl flex items-center gap-2">
              <Shield size={20} /> Authentication Required
            </CardTitle>
            <CardDescription>
              Please sign in to access admin features.
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button 
              onClick={() => window.location.href = '/login'} 
              className="w-full"
            >
              Sign In
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center min-h-[60vh] p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl flex items-center gap-2">
            <Lock size={20} /> Admin Access
          </CardTitle>
          <CardDescription>
            Enter the admin password for temporary access, or contact an administrator to be granted permanent admin privileges.
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent>
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter admin password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError('');
                  }}
                />
                {error && <p className="text-sm text-red-500">{error}</p>}
              </div>
            </div>
          </CardContent>
          <CardFooter>
            <Button type="submit" className="w-full">Request Access</Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default SecureAdminAccess;
